import { useState, type FormEvent } from 'react';
import { updatePassword } from 'aws-amplify/auth';
import PasswordRequirements from './PasswordRequirements';

interface Props {
  isOAuthUser: boolean;
}

export default function ChangePasswordForm({ isOAuthUser }: Props) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [newPwdFocused, setNewPwdFocused] = useState(false);

  if (isOAuthUser) {
    return (
      <p className="settings-oauth-msg">
        Password is managed by your identity provider (Google/LinkedIn).
      </p>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPwd !== confirmPwd) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await updatePassword({ oldPassword: currentPassword, newPassword: newPwd });
      setSuccess('Password updated');
      setCurrentPassword('');
      setNewPwd('');
      setConfirmPwd('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="settings-form">
      <div className="field">
        <label className="field-label" htmlFor="current-password">
          Current Password
        </label>
        <input
          id="current-password"
          className="input"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          autoComplete="current-password"
          placeholder="Enter current password"
        />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="new-password">
          New Password
        </label>
        <input
          id="new-password"
          className="input"
          type="password"
          value={newPwd}
          onChange={(e) => setNewPwd(e.target.value)}
          onFocus={() => setNewPwdFocused(true)}
          onBlur={() => setNewPwdFocused(false)}
          required
          autoComplete="new-password"
          placeholder="8+ chars, uppercase, digit"
          minLength={8}
        />
        <PasswordRequirements password={newPwd} visible={newPwdFocused || newPwd.length > 0} />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="confirm-password">
          Confirm New Password
        </label>
        <input
          id="confirm-password"
          className="input"
          type="password"
          value={confirmPwd}
          onChange={(e) => setConfirmPwd(e.target.value)}
          required
          autoComplete="new-password"
          placeholder="Re-enter new password"
        />
      </div>

      {success && <p className="success-msg">{success}</p>}
      {error && <p className="error-msg">{error}</p>}

      <button type="submit" className="btn btn-primary" disabled={loading}>
        {loading ? 'Updating...' : 'Update Password'}
      </button>
    </form>
  );
}
